import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, CircularProgress, Grid, IconButton,
  Tooltip, InputAdornment, Avatar, LinearProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Search, FileDownload, Refresh, ArrowBack, LocationOn,
  Schedule, TrendingUp, ElectricScooter, GpsOff, GpsFixed,
  Assessment, Person, DirectionsCar, Map as MapIcon,
  CheckCircle, Error as ErrorIcon, Sync,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';

const MAP_LAYERS = {
  street:    { label: 'Street',    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',            attribution: '© OpenStreetMap', maxZoom: 19 },
  satellite: { label: 'Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri', maxZoom: 19 },
  terrain:   { label: 'Terrain',   url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',              attribution: '© OpenTopoMap', maxZoom: 17 },
  dark:      { label: 'Dark',      url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CartoDB', maxZoom: 19 },
};

const Stat = ({ icon, label, value, color }) => (
  <Card sx={{ borderRadius: 1, border: `1px solid ${color}22`, bgcolor: `${color}08`, boxShadow: 'none' }}>
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ width: 36, height: 36, bgcolor: `${color}20`, color }}>{icon}</Avatar>
        <Box>
          <Typography fontSize={10} color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>{label}</Typography>
          <Typography fontWeight={800} fontSize={15} sx={{ color }}>{value}</Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const fmtMin = m => {
  const n = parseFloat(m) || 0;
  if (n === 0) return '0 min';
  if (n < 60) return `${n} min`;
  return `${Math.floor(n/60)}h ${Math.round(n%60)}m`;
};

export default function FleetMovementReport() {
  const navigate = useNavigate();

  const [loading,     setLoading]     = useState(false);
  const [data,        setData]        = useState([]);
  const [stats,       setStats]       = useState(null);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [date,        setDate]        = useState(new Date().toISOString().split('T')[0]);

  // ── Sync state ───────────────────────────────────────────────────────────────
  const [syncDialog,   setSyncDialog]   = useState(false);
  const [syncRunning,  setSyncRunning]  = useState(false);
  const [syncDone,     setSyncDone]     = useState(false);
  const [syncCurrent,  setSyncCurrent]  = useState(0);
  const [syncTotal,    setSyncTotal]    = useState(0);
  const [syncVehicle,  setSyncVehicle]  = useState('');  // current vehicle being synced
  const [syncResults,  setSyncResults]  = useState([]);  // [{vehicle, stored, distance_km}]
  const [syncErrors,   setSyncErrors]   = useState([]);
  const syncCancelRef = useRef(false);
  const [search,      setSearch]      = useState('');

  // Path dialog (matches VehicleDetail design)
  const [pathDialog,  setPathDialog]  = useState(false);
  const [selVehicle,  setSelVehicle]  = useState(null);
  const [pathLoading, setPathLoading] = useState(false);
  const [pathPoints,  setPathPoints]  = useState([]);
  const [pathStats,   setPathStats]   = useState(null);
  const [pathMapLayer,setPathMapLayer]= useState('street');
  const [pathError,   setPathError]   = useState(null);
  const pathMapRef    = useRef(null);
  const leafletMapRef = useRef(null);
  const tileLayerRef  = useRef(null);

  // ── Fetch fleet data ─────────────────────────────────────────────────────────
  // Strategy:
  //  1. Try DB stats (fast, no rate limit)
  //  2. If DB has no meaningful data (empty or all zeros), fall back to live fleet-report
  const fetchData = useCallback(async () => {
    setLoading(true);
    const controller = new AbortController();
    try {
      // Step 1: Try DB stats
      const [dbRes, vRes] = await Promise.all([
        InvokeApi(Config.base_url + apiList.loconavDbStats, { date }),
        InvokeApi(Config.base_url + apiList.loconavVehicles, { page: 1, per_page: 200 }),
      ]);

      if (controller.signal.aborted) return;

      const dbStats   = dbRes?.stats || [];
      // Use DB data if ANY records exist for this date — even if all vehicles show 0 km (parked day is valid)
      const hasDbData = dbStats.length > 0;

      if (hasDbData) {
        // ── Use DB data directly — DB is the source of truth after sync ──────
        const statsMap = {};
        dbStats.forEach(s => { statsMap[s.vehicle_number] = s; });

        // Enrich DB records with vehicle info from Loconav vehicles list
        const vehMap = {};
        (vRes?.vehicles || []).forEach(v => { vehMap[v.number] = v; });

        // Build vehicle list from DB stats (not from Loconav list) so parked vehicles appear
        const vehicles = dbStats.map(s => {
          const v = vehMap[s.vehicle_number] || {};
          return {
            loconav_id:        v.id            || null,
            vehicle_uuid:      v.vehicleUuid   || s.vehicle_uuid || null,
            vehicle_number:    s.vehicle_number,
            db_vehicle_id:     v.db_vehicle_id || null,
            user_id:           v.user_id        || null,
            model:             v.model          || v.vehicleType || '—',
            assigned_user:     v.assigned_user  || null,
            assigned_phone:    v.assigned_phone || null,
            vendor_name:       v.vendor_name    || null,
            distance_km:       parseFloat(s.distance_km   || 0),
            running_time_min:  parseFloat(s.running_min   || 0),
            idle_duration_min: parseFloat(s.idle_min      || 0),
            stoppage_time_min: parseFloat(s.stoppage_min  || 0),
            overall_score:     parseFloat(s.overall_score || 0),
            movement_status:   'UNKNOWN',
          };
        }).filter(v => search ? v.vehicle_number.toLowerCase().includes(search.toLowerCase()) : true)
          .sort((a, b) => b.distance_km - a.distance_km); // Sort by distance descending

        const totalDist    = vehicles.reduce((s, v) => s + v.distance_km, 0);
        const totalRunning = vehicles.reduce((s, v) => s + v.running_time_min, 0);
        const totalIdle    = vehicles.reduce((s, v) => s + v.idle_duration_min, 0);

        setData(vehicles);
        setStats({
          total_vehicles:    vehicles.length,
          total_distance:    totalDist.toFixed(2),
          total_running_min: totalRunning.toFixed(1),
          total_idle_min:    totalIdle.toFixed(1),
        });
        setTotal(vehicles.length);
      } else {
        // ── No DB data or all zeros — fall back to live fleet-report ─────────
        console.info(`[FleetReport] No DB records for ${date} — fetching live data...`);
        const liveRes = await InvokeApi(Config.base_url + apiList.loconavFleetReport, {
          date,
          page: 1,
          per_page: 200,
          search,
        });

        if (controller.signal.aborted) return;

        if (liveRes?.code === '200' && liveRes.vehicles?.length > 0) {
          const vehicles = liveRes.vehicles.filter(v =>
            search ? v.vehicle_number.toLowerCase().includes(search.toLowerCase()) : true
          );
          setData(vehicles);
          setStats(liveRes.stats || null);
          setTotal(liveRes.total || vehicles.length);
        } else {
          // No data at all
          setData([]); setStats(null); setTotal(0);
        }
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error('Fleet report fetch failed:', err);
      setData([]); setStats(null); setTotal(0);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
    return () => controller.abort();
  }, [date, page, rowsPerPage, search]);

  // Auto-fetch on date/page/rowsPerPage change, not on every keystroke
  useEffect(() => { fetchData(); }, [date, page, rowsPerPage]); 

  // ── CSV ───────────────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const headers = ['Vehicle Number', 'Model', 'Vendor', 'Assigned To', 'Distance (km)', 'Running Time', 'Idle Time'];
    const rows = data.map(v => [
      v.vehicle_number          || '—',
      v.model                   || '—',
      v.vendor_name             || '—',
      v.assigned_user           || '—',
      v.distance_km             || 0,
      fmtMin(v.running_time_min || 0),
      fmtMin(v.idle_duration_min|| 0),
    ]);
    const bom = '\uFEFF';
    const csv = bom + [headers, ...rows]
      .map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = `fleet-report-${date}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // ── One-by-one vehicle sync ───────────────────────────────────────────────────
  const startSync = async (forceResync = false) => {
    syncCancelRef.current = false;
    setSyncRunning(true);
    setSyncDone(false);
    setSyncCurrent(0);
    setSyncResults([]);
    setSyncErrors([]);
    setSyncVehicle('');

    // 1. Fetch all vehicles
    const vRes = await InvokeApi(Config.base_url + apiList.loconavVehicles, { page: 1, per_page: 999 });
    const vehicles = vRes?.vehicles || [];
    setSyncTotal(vehicles.length);

    if (vehicles.length === 0) {
      setSyncRunning(false);
      setSyncErrors(['No vehicles found from Loconav']);
      return;
    }

    const results = [];
    const errors  = [];

    // 2. Process one vehicle at a time
    for (let i = 0; i < vehicles.length; i++) {
      if (syncCancelRef.current) break;

      const v = vehicles[i];
      setSyncVehicle(v.number);
      setSyncCurrent(i + 1);

      try {
        const res = await InvokeApi(Config.base_url + apiList.loconavSyncVehicle, {
          vehicle_number: v.number,
          vehicle_uuid:   v.vehicleUuid,
          date,
          force:          forceResync, // true = always re-fetch from Loconav, false = skip if recent
        });

        results.push({
          vehicle:     v.number,
          stored:      res?.code === '200',
          skipped:     res?.skipped ?? false,
          distance_km: res?.distance_km ?? 0,
          running_min: res?.running_min ?? 0,
          error:       res?.code !== '200' ? (res?.message || 'Failed') : null,
        });

        if (res?.code !== '200') {
          errors.push(`${v.number}: ${res?.message || 'Failed'}`);
        }
      } catch (err) {
        results.push({ vehicle: v.number, stored: false, skipped: false, distance_km: 0, error: err.message });
        errors.push(`${v.number}: ${err.message}`);
      }

      setSyncResults([...results]);
      setSyncErrors([...errors]);
    }

    setSyncRunning(false);
    setSyncDone(true);
    setSyncVehicle('');

    // 3. Refresh the fleet data after sync
    if (!syncCancelRef.current) {
      fetchData();
    }
  };

  // ── Open path dialog ─────────────────────────────────────────────────────────
  const openPathDrawer = async (vehicle) => {
    setSelVehicle(vehicle); setPathDialog(true);
    setPathLoading(true); setPathPoints([]); setPathStats(null); setPathError(null);
    try {
      const s = Math.floor(new Date(date+'T00:00:00').getTime()/1000);
      const e = Math.floor(new Date(date+'T23:59:59').getTime()/1000);

      // 1. Try local DB first (fast, no rate limit)
      const dbRes = await InvokeApi(Config.base_url + apiList.loconavDbPath, {
        vehicle_number: vehicle.vehicle_number,
        date,
      });

      if (dbRes?.code === '200' && dbRes.points?.length > 0) {
        setPathPoints(dbRes.points.filter(p => p.lat && p.lng));
        setPathStats(dbRes.stats);
        setPathLoading(false);
        return;
      }

      // 2. Fallback: live Loconav timeline API
      const res = await InvokeApi(Config.base_url + apiList.loconavTimeline, {
        vehicle_uuid: vehicle.vehicle_uuid, start_time: s, end_time: e,
      });
      if (res?.code === '200') {
        const pts = (res.points||[]).filter(p=>p.lat&&p.lng);
        if (pts.length > 0) {
          setPathPoints(pts);
          setPathStats(res.stats || null);
          // Auto-save to DB for future use
          InvokeApi(Config.base_url + apiList.loconavSyncDay, { date }).catch(() => {});
        } else {
          setPathError('No GPS path data for this date.');
        }
      } else {
        setPathError(res?.message || 'No timeline data.');
      }
    } catch(err) { setPathError('Error: '+err.message); }
    finally { setPathLoading(false); }
  };

  const closeDrawer = () => {
    if (leafletMapRef.current) { leafletMapRef.current.remove(); leafletMapRef.current=null; tileLayerRef.current=null; }
    setPathDialog(false); setSelVehicle(null); setPathPoints([]); setPathStats(null); setPathError(null);
    setPathMapLayer('street');
  };

  // ── Leaflet ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!pathDialog || pathLoading || pathPoints.length===0 || !pathMapRef.current) return;
    if (leafletMapRef.current) { leafletMapRef.current.remove(); leafletMapRef.current=null; tileLayerRef.current=null; }

    const init = () => {
      const L=window.L; if(!L) return;
      const coords = pathPoints.map(p=>[p.lat,p.lng]);
      const map    = L.map(pathMapRef.current).fitBounds(coords);
      leafletMapRef.current = map;
      const ly=MAP_LAYERS[pathMapLayer]||MAP_LAYERS.street;
      tileLayerRef.current=L.tileLayer(ly.url,{attribution:ly.attribution,maxZoom:ly.maxZoom}).addTo(map);
      L.polyline(coords,{color:'#1a237e',weight:4,opacity:.85}).addTo(map);
      const mk=col=>L.divIcon({html:`<div style="background:${col};width:12px;height:12px;border-radius:50%;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.4)"></div>`,iconSize:[12,12],iconAnchor:[6,6],className:''});
      // Find first/last point with an actual timestamp (many points have null)
      const fmtTs = ts => {
        if (!ts) return '';
        const ms = typeof ts === 'number' ? (ts < 1e10 ? ts * 1000 : ts) : new Date(String(ts).replace(' ','T')).getTime();
        const d = new Date(ms);
        return isNaN(d.getTime()) ? '' : '<br>' + d.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',second:'2-digit'});
      };
      const firstTs = (pathPoints.find(p => p.timestamp) || {}).timestamp;
      const lastTs  = ([...pathPoints].reverse().find(p => p.timestamp) || {}).timestamp;
      L.marker(coords[0],{icon:mk('#2e7d32')}).addTo(map).bindPopup('<b>Start</b>' + fmtTs(firstTs)).openPopup();
      L.marker(coords[coords.length-1],{icon:mk('#c62828')}).addTo(map).bindPopup('<b>End</b>' + fmtTs(lastTs));
    };

    if (!window.L) {
      if (!document.getElementById('lf-css')) { const l=document.createElement('link');l.id='lf-css';l.rel='stylesheet';l.href='https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';document.head.appendChild(l); }
      if (!document.getElementById('lf-js'))  { const s=document.createElement('script');s.id='lf-js';s.src='https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';s.onload=init;document.head.appendChild(s); }
    } else init();

    return () => { if (leafletMapRef.current) { leafletMapRef.current.remove(); leafletMapRef.current=null; tileLayerRef.current=null; } };
  }, [pathDialog, pathLoading, pathPoints]);

  useEffect(() => {
    const L=window.L; if(!L||!leafletMapRef.current||!tileLayerRef.current) return;
    const ly=MAP_LAYERS[pathMapLayer]||MAP_LAYERS.street;
    leafletMapRef.current.removeLayer(tileLayerRef.current);
    tileLayerRef.current=L.tileLayer(ly.url,{attribution:ly.attribution,maxZoom:ly.maxZoom}).addTo(leafletMapRef.current);
  },[pathMapLayer]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display:'flex', alignItems:'center', gap:2, mb:2.5, flexWrap:'wrap' }}>
        <Tooltip title="Back">
          <IconButton onClick={() => navigate(-1)} sx={{ border:'1px solid #e8eaf6', borderRadius:2 }}>
            <ArrowBack sx={{ color:'#1a237e' }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex:1 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Fleet Movement Report</Typography>
          <Typography variant="body2" color="text.secondary">{date} · {total} vehicles</Typography>
        </Box>
        <Button variant="outlined" startIcon={<FileDownload />} onClick={exportCSV} disabled={!data.length}
          sx={{ borderColor:'#1a237e', color:'#1a237e', fontWeight:600 }}>CSV</Button>
        <Tooltip title={`Sync ${date} data to DB — scans each vehicle one by one`}>
          <Button variant="outlined" startIcon={<Sync />} onClick={() => { setSyncDialog(true); setSyncDone(false); setSyncCurrent(0); setSyncResults([]); setSyncErrors([]); }}
            sx={{ borderColor:'#16a34a', color:'#16a34a', fontWeight:600 }}>
            Sync to DB
          </Button>
        </Tooltip>
      </Box>

      {/* Stats */}
      {stats && (
        <Grid
        d container spacing={1.5} sx={{ mb:2 }}>
          {[
            {label:'Vehicles',      value:stats.total_vehicles      ||0,                           color:'#1a237e', icon:<ElectricScooter/>},
            {label:'Distance',      value:`${stats.total_distance   ||0} km`,                      color:'#0284c7', icon:<TrendingUp/>},
            {label:'Total Running', value:fmtMin(stats.total_running_min||0),                      color:'#16a34a', icon:<Schedule/>},
            {label:'Total Idle',    value:fmtMin(stats.total_idle_min   ||0),                      color:'#d97706', icon:<GpsFixed/>},
          ].map(s=><Grid item xs={6} sm={3} md={3} key={s.label}><Stat {...s}/></Grid>)}
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ borderRadius:1, boxShadow:'0 2px 8px rgba(0,0,0,.06)', mb:2 }}>
        <CardContent sx={{ p:1.5, '&:last-child':{pb:1.5} }}>
          <Box sx={{ display:'flex', gap:1.5, flexWrap:'wrap', alignItems:'center' }}>
            <TextField size="small" type="date" label="Date" value={date}
              onChange={e=>{setDate(e.target.value);setPage(0);}}
              InputLabelProps={{shrink:true}} sx={{ minWidth:155 }} />
            <TextField size="small" placeholder="Search vehicle / user…" value={search}
              onChange={e=>setSearch(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&fetchData()}
              InputProps={{startAdornment:<InputAdornment position="start"><Search fontSize="small"/></InputAdornment>}}
              sx={{ flex:1, minWidth:200 }} />
            <Button variant="contained" onClick={()=>{setPage(0);fetchData();}}
              sx={{ background:'linear-gradient(135deg,#1a237e,#1565c0)', fontWeight:600 }}>Search</Button>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData} sx={{ border:'1px solid #e8eaf6', borderRadius:1.5 }}>
                <Refresh sx={{ color:'#1a237e', fontSize:18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius:1, boxShadow:'0 4px 20px rgba(0,0,0,.08)' }}>
        {loading ? (
          <Box sx={{ display:'flex', justifyContent:'center', p:8 }}>
            <CircularProgress sx={{ color:'#1a237e' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor:'#f5f6fa' }}>
                    {['Vehicle','Distance','Running','Idle','Actions'].map(h=>(
                      <TableCell key={h} sx={{ fontWeight:700, color:'#1a237e', fontSize:11, py:1.2, whiteSpace:'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length===0 ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py:6, color:'text.secondary' }}>
                      {`No synced data for ${date}. Click "Sync to DB" to load data.`}
                    </TableCell></TableRow>
                  ) : data.map(v => (
                    <TableRow key={v.vehicle_number} hover>

                      {/* Vehicle col */}
                      <TableCell>
                        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                          <Avatar
                            onClick={() => v.db_vehicle_id && navigate(`/vehicle/${v.db_vehicle_id}`)}
                            sx={{
                              width:32, height:32, flexShrink:0,
                              background: v.db_vehicle_id ? 'linear-gradient(135deg,#1a237e,#1565c0)' : '#e2e8f0',
                              cursor: v.db_vehicle_id ? 'pointer' : 'default',
                              '&:hover': v.db_vehicle_id ? { opacity:0.8 } : {},
                            }}
                          >
                            <DirectionsCar sx={{ fontSize:16, color: v.db_vehicle_id ? '#fff' : '#94a3b8' }} />
                          </Avatar>
                          <Box>
                            <Typography fontSize={12} fontWeight={700}
                              sx={{ fontFamily:'monospace', cursor: v.db_vehicle_id?'pointer':'default',
                                '&:hover': v.db_vehicle_id?{color:'#1565c0',textDecoration:'underline'}:{} }}
                              onClick={() => v.db_vehicle_id && navigate(`/vehicle/${v.db_vehicle_id}`)}>
                              {v.vehicle_number}
                            </Typography>
                            <Typography fontSize={10} color="text.secondary">{v.model||'—'}</Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Distance */}
                      <TableCell>
                        <Typography fontSize={13} fontWeight={700} color={v.distance_km>0?'#0284c7':'text.disabled'}>
                          {v.distance_km||0} km
                        </Typography>
                      </TableCell>

                      {/* Running */}
                      <TableCell sx={{ fontSize:12, color:'text.secondary' }}>{fmtMin(v.running_time_min||0)}</TableCell>

                      {/* Idle */}
                      <TableCell sx={{ fontSize:12, color: v.idle_duration_min>30?'#d97706':'text.secondary' }}>
                        {fmtMin(v.idle_duration_min||0)}
                      </TableCell>

                      {/* Actions — vehicle icon + user icon + path icon */}
                      <TableCell>
                        <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                          {/* Vehicle detail */}
                          <Tooltip title={v.db_vehicle_id ? `${v.vehicle_number} — Vehicle Detail` : 'Not in DB'}>
                            <span>
                              <IconButton size="small" disabled={!v.db_vehicle_id}
                                onClick={() => navigate(`/vehicle/${v.db_vehicle_id}`)}
                                sx={{ color:'#1a237e', '&:disabled':{color:'#e2e8f0'} }}>
                                <DirectionsCar sx={{ fontSize:16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {/* User detail */}
                          <Tooltip title={v.user_id ? `👤 ${v.assigned_user} — View User` : 'No user assigned'}>
                            <span>
                              <IconButton size="small" disabled={!v.user_id}
                                onClick={() => navigate(`/user/${v.user_id}`)}
                                sx={{ color:'#0369a1', '&:disabled':{color:'#e2e8f0'} }}>
                                <Person sx={{ fontSize:16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {/* Path */}
                          <Tooltip title={v.vehicle_uuid ? `Path on ${date}` : 'No GPS'}>
                            <span>
                              <IconButton size="small" disabled={!v.vehicle_uuid} onClick={() => openPathDrawer(v)}
                                sx={{ color:'#1565c0', '&:disabled':{color:'#e2e8f0'} }}>
                                <LocationOn sx={{ fontSize:16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={total} page={page}
              onPageChange={(_,p)=>setPage(p)} rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e=>{setRowsPerPage(parseInt(e.target.value,10));setPage(0);}}
              rowsPerPageOptions={[25,50,100]} sx={{ borderTop:'1px solid #f0f0f0' }} />
          </>
        )}
      </Card>

      {/* ── GPS Track Path Dialog (matches VehicleDetail design) ── */}
      <Dialog open={pathDialog} onClose={closeDrawer} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#0277bd', borderBottom: '1px solid #f0f0f0', pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MapIcon sx={{ color: '#0277bd' }} />
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={700} color="#0277bd">
                GPS Track Path — {selVehicle?.vehicle_number}
              </Typography>
              <Typography fontSize={12} color="text.secondary">{date}</Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {pathLoading ? (
            <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', py:10, gap:2 }}>
              <CircularProgress sx={{ color:'#0277bd' }} />
              <Typography color="text.secondary" fontSize={13}>Loading GPS path…</Typography>
            </Box>
          ) : pathError ? (
            <Box sx={{ textAlign:'center', py:8, px:3 }}>
              <GpsOff sx={{ fontSize:56, color:'#e8eaf6', mb:1.5 }} />
              <Typography color="text.secondary" fontWeight={600} fontSize={15}>No path data available</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display:'block', mt:0.5 }}>{pathError}</Typography>
            </Box>
          ) : pathPoints.length > 0 ? (
            <Box>
              {/* Start/End time + layer switcher bar */}
              <Box sx={{ display:'flex', gap:2, px:2.5, py:1.5, background:'#e3f2fd', borderBottom:'1px solid #bbdefb', flexWrap:'wrap', alignItems:'center' }}>
                {(() => {
                  // Find first and last points that actually have a timestamp
                  const fmtEpoch = ts => {
                    if (!ts) return null;
                    const ms = typeof ts === 'number' ? (ts < 1e10 ? ts * 1000 : ts) : new Date(String(ts).replace(' ','T')).getTime();
                    const d = new Date(ms);
                    return isNaN(d.getTime()) ? null : d.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',second:'2-digit'});
                  };
                  const firstPt = pathPoints.find(p => p.timestamp);
                  const lastPt  = [...pathPoints].reverse().find(p => p.timestamp);
                  const startStr = firstPt ? fmtEpoch(firstPt.timestamp) : null;
                  const endStr   = lastPt  ? fmtEpoch(lastPt.timestamp)  : null;
                  return (
                    <>
                      {startStr && (
                        <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                          <Box sx={{ width:10, height:10, borderRadius:'50%', background:'#2e7d32', border:'2px solid #fff', boxShadow:'0 1px 3px rgba(0,0,0,.3)' }} />
                          <Typography fontSize={12} fontWeight={600} color="#2e7d32">Start: {startStr}</Typography>
                        </Box>
                      )}
                      {endStr && (
                        <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                          <Box sx={{ width:10, height:10, borderRadius:'50%', background:'#c62828', border:'2px solid #fff', boxShadow:'0 1px 3px rgba(0,0,0,.3)' }} />
                          <Typography fontSize={12} fontWeight={600} color="#c62828">End: {endStr}</Typography>
                        </Box>
                      )}
                    </>
                  );
                })()}
                {/* Layer switcher */}
                <Box sx={{ ml:'auto', display:'flex', gap:0.5 }}>
                  {Object.entries(MAP_LAYERS).map(([key, val]) => (
                    <Button key={key} size="small"
                      variant={pathMapLayer === key ? 'contained' : 'outlined'}
                      onClick={() => setPathMapLayer(key)}
                      sx={{ fontSize:10, fontWeight:700, px:1, py:0.3, minWidth:0, borderRadius:1.5,
                        ...(pathMapLayer===key
                          ? { background:'#0277bd', color:'#fff', borderColor:'#0277bd' }
                          : { color:'#0277bd', borderColor:'#90caf9', background:'#fff' }) }}>
                      {val.label}
                    </Button>
                  ))}
                </Box>
              </Box>

              {/* Stats strip — Distance / Running / Idle / Avg Speed */}
              {(pathStats || selVehicle) && (() => {
                // pathStats keys (from /loconav/timeline or /loconav/db-path):
                //   distance_km, running_time_min, idle_min, avg_speed_kmh
                // selVehicle keys (from fleet-report score card):
                //   distance_km, running_time_min, idle_duration_min  (no avg_speed)
                const dist    = pathStats?.distance_km      ?? selVehicle?.distance_km      ?? 0;
                const runMin  = pathStats?.running_time_min ?? selVehicle?.running_time_min ?? 0;
                const idleMin = pathStats?.idle_min         ?? selVehicle?.idle_duration_min ?? 0;
                const avgSpd  = pathStats?.avg_speed_kmh    ?? 0;
                const fmtMin  = m => m >= 60 ? `${Math.floor(m/60)}h ${Math.round(m%60)}m` : `${m} min`;
                return (
                  <Box sx={{ display:'flex', borderBottom:'1px solid #e2e8f0' }}>
                    {[
                      {label:'Distance',  val:`${dist} km`,      color:'#0284c7'},
                      {label:'Running',   val:fmtMin(runMin),     color:'#16a34a'},
                      {label:'Idle',      val:fmtMin(idleMin),    color:'#d97706'},
                      {label:'Avg Speed', val:`${avgSpd} km/h`,   color:'#7c3aed'},
                    ].map(s=>(
                      <Box key={s.label} sx={{ flex:1, p:1.5, borderRight:'1px solid #e2e8f0', '&:last-child':{borderRight:'none'} }}>
                        <Typography fontSize={10} color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase' }}>{s.label}</Typography>
                        <Typography fontSize={14} fontWeight={800} sx={{ color:s.color }}>{s.val}</Typography>
                      </Box>
                    ))}
                  </Box>
                );
              })()}

              {/* Leaflet map */}
              <Box ref={pathMapRef} sx={{ width:'100%', height:460 }} />
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ p:2, borderTop:'1px solid #f0f0f0' }}>
          <Button onClick={closeDrawer} variant="outlined" sx={{ borderRadius:2 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Sync to DB Dialog — one vehicle at a time with live progress ── */}
      <Dialog open={syncDialog} maxWidth="sm" fullWidth disableEscapeKeyDown={syncRunning}
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Sync sx={{ color: '#16a34a', fontSize: 22 }} />
            <Box>
              <Typography fontWeight={800} color="#1a237e">Sync Fleet Data to DB</Typography>
              <Typography fontSize={12} color="text.secondary">{date} · {syncTotal} vehicles</Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Progress bar */}
          {(syncRunning || syncDone) && (
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography fontSize={12} color="text.secondary" fontWeight={600}>
                  {syncDone ? 'Sync complete!' : `Scanning: ${syncVehicle || '...'}`}
                </Typography>
                <Typography fontSize={12} fontWeight={700} color="#1a237e">
                  {syncCurrent} / {syncTotal}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={syncTotal > 0 ? (syncCurrent / syncTotal) * 100 : 0}
                sx={{ height: 10, borderRadius: 5,
                  '& .MuiLinearProgress-bar': {
                    background: syncDone ? 'linear-gradient(90deg,#16a34a,#22c55e)' : 'linear-gradient(90deg,#1a237e,#1565c0)',
                  }
                }}
              />
            </Box>
          )}

          {/* Not started yet */}
          {!syncRunning && !syncDone && (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Typography color="text.secondary" fontSize={14} mb={1}>
                This will scan each of the <strong>{syncTotal || '?'}</strong> vehicles one by one
                and store their movement data for <strong>{date}</strong>.
              </Typography>
              <Typography color="text.secondary" fontSize={12}>
                Vehicles with no movement are skipped. Only actual movement data is stored.
              </Typography>
            </Box>
          )}

          {/* Results list */}
          {syncResults.length > 0 && (
            <Box sx={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 1.5, mt: 1 }}>
              {syncResults.map((r, i) => (
                <Box key={r.vehicle} sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 2, py: 1,
                  borderBottom: i < syncResults.length - 1 ? '1px solid #f5f5f5' : 'none',
                  bgcolor: r.error ? '#fff5f5' : 'transparent',
                }}>
                  {r.error
                    ? <ErrorIcon sx={{ fontSize: 16, color: '#dc2626', flexShrink: 0 }} />
                    : <CheckCircle sx={{ fontSize: 16, color: r.distance_km > 0 ? '#16a34a' : '#94a3b8', flexShrink: 0 }} />
                  }
                  <Typography fontSize={12} fontFamily="monospace" fontWeight={600} sx={{ flex: 1 }}>
                    {r.vehicle}
                  </Typography>
                  {r.error
                    ? <Typography fontSize={11} color="error">{r.error}</Typography>
                    : r.distance_km > 0
                      ? <Chip label={`${r.distance_km} km`} size="small" color="success" variant="outlined"
                          sx={{ fontSize: 10, height: 20 }} />
                      : <Chip label="Parked (0 km)" size="small" color="default" variant="outlined"
                          sx={{ fontSize: 10, height: 20, color: '#94a3b8', borderColor: '#e2e8f0' }} />
                  }
                </Box>
              ))}
            </Box>
          )}

          {/* Summary after done */}
          {syncDone && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdf4', borderRadius: 1.5, border: '1px solid #bbf7d0' }}>
              <Typography fontWeight={700} color="#16a34a" fontSize={14}>
                ✓ Sync complete — {syncResults.filter(r => r.stored).length} vehicles stored,{' '}
                {syncResults.filter(r => !r.stored && !r.error).length} skipped (no movement),{' '}
                {syncErrors.length} errors
              </Typography>
            </Box>
          )}

          {/* Errors summary */}
          {syncErrors.length > 0 && (
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff5f5', borderRadius: 1.5, border: '1px solid #fecaca' }}>
              <Typography fontSize={11} color="error" fontWeight={700}>{syncErrors.length} error(s):</Typography>
              {syncErrors.slice(0, 3).map((e, i) => (
                <Typography key={i} fontSize={11} color="error">{e}</Typography>
              ))}
              {syncErrors.length > 3 && (
                <Typography fontSize={11} color="error">…and {syncErrors.length - 3} more</Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          {!syncRunning && !syncDone && (
            <>
              <Button onClick={() => setSyncDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>
                Cancel
              </Button>
              <Button onClick={() => startSync(false)} variant="contained" startIcon={<Sync />}
                sx={{ background: 'linear-gradient(135deg,#16a34a,#15803d)', borderRadius: 2, fontWeight: 700 }}>
                Smart Sync
              </Button>
              <Button onClick={() => startSync(true)} variant="outlined"
                sx={{ borderColor: '#1a237e', color: '#1a237e', borderRadius: 2, fontWeight: 700 }}>
                Force Re-sync
              </Button>
            </>
          )}
          {syncRunning && (
            <Button onClick={() => { syncCancelRef.current = true; }}
              variant="outlined" color="error" sx={{ borderRadius: 2 }}>
              Cancel Sync
            </Button>
          )}
          {syncDone && (
            <Button onClick={() => setSyncDialog(false)} variant="contained"
              sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
